import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from '@/lib/router-compat';
import { 
  Home, 
  Car, 
  GraduationCap, 
  Briefcase, 
  CreditCard, 
  PiggyBank,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export default function Services() {
  const loanTypes = [
    {
      icon: Home,
      title: 'Personal Loans',
      description: 'Flexible personal loans for any purpose. Consolidate debt, fund home improvements, or cover unexpected expenses.',
      rates: 'From 6.99% APR',
      amounts: '$5,000 - $100,000',
      features: ['No collateral required', 'Fixed monthly payments', 'Terms up to 7 years'],
    },
    {
      icon: Car,
      title: 'Auto Loans',
      description: 'Finance your next vehicle with competitive rates. New or used, we have options for you.',
      rates: 'From 4.49% APR',
      amounts: '$5,000 - $150,000',
      features: ['New & used vehicles', 'Quick pre-approval', 'Refinancing available'],
    },
    {
      icon: Home,
      title: 'Home Equity Loans',
      description: 'Tap into your home\'s equity for major expenses or investments with secured rates.',
      rates: 'From 5.99% APR',
      amounts: '$25,000 - $500,000',
      features: ['Tax-deductible interest', 'Lower rates than unsecured', 'Large loan amounts'],
    },
    {
      icon: GraduationCap,
      title: 'Student Loans',
      description: 'Invest in your education with flexible student loan options and deferred payment plans.',
      rates: 'From 3.99% APR',
      amounts: '$1,000 - $200,000',
      features: ['Deferred payments', 'No origination fees', 'Cosigner release option'],
    },
    {
      icon: Briefcase,
      title: 'Business Loans',
      description: 'Fuel your business growth with capital for expansion, equipment, or working capital.',
      rates: 'From 7.99% APR',
      amounts: '$10,000 - $500,000',
      features: ['Quick funding', 'Flexible terms', 'No prepayment penalty'],
    },
    {
      icon: CreditCard,
      title: 'Debt Consolidation',
      description: 'Simplify your finances by combining multiple debts into one manageable payment.',
      rates: 'From 5.99% APR',
      amounts: '$5,000 - $100,000',
      features: ['Lower overall rate', 'Single monthly payment', 'Improve credit score'],
    },
  ];

  const benefits = [
    {
      icon: PiggyBank,
      title: 'Competitive Rates',
      description: 'We offer some of the most competitive rates in the industry, saving you money over the life of your loan.',
    },
    {
      title: 'Fast Approval',
      description: 'Get pre-approved in minutes and funded in as little as 24 hours.',
    },
    {
      title: 'No Hidden Fees',
      description: 'Transparent pricing with no origination fees, prepayment penalties, or hidden charges.',
    },
    {
      title: 'Flexible Terms',
      description: 'Choose repayment terms that work for your budget, from 12 to 84 months.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 gradient-hero">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">
              Our Loan Services
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              Find the perfect loan for your needs. From personal loans to business financing, 
              we offer competitive rates and flexible terms.
            </p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
              <Link to="/apply">
                Start Your Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Loan Types Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl font-bold text-foreground mb-4">
              Loan Options
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose from our range of loan products designed to meet your specific needs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loanTypes.map((loan, index) => (
              <div 
                key={index} 
                className="bg-background border border-border rounded-xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <loan.icon className="h-6 w-6 text-primary-foreground" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">{loan.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{loan.description}</p>
                
                <div className="space-y-2 mb-4 pb-4 border-b border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rates</span>
                    <span className="font-semibold text-accent">{loan.rates}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amounts</span>
                    <span className="font-medium text-foreground">{loan.amounts}</span>
                  </div>
                </div>
                
                <ul className="space-y-2 mb-6">
                  {loan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button asChild variant="outline" className="w-full group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent transition-colors">
                  <Link to="/apply">Apply Now</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl font-bold text-foreground mb-4">
              Why Choose Quick Credit?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We're committed to making your borrowing experience simple and rewarding.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-accent" />
                </div>
                <h3 className="font-display font-semibold text-foreground mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-display text-3xl font-bold text-primary-foreground mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              Apply now and get a decision in minutes. Our simple application process 
              makes borrowing easy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/apply">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
